/**
 * Analytics - Google Tag Manager integration for analytics tracking
 * Handles impression and click tracking for cards, ads, and affiliates
 * 
 * GDPR Compliance:
 * - Only tracks if user has accepted analytics cookies
 * - If user rejects analytics cookies, NO tracking occurs
 * - Privacy-first: defaults to no tracking if consent system unavailable
 */

import { LOGGING, ANALYTICS_SETTINGS } from '/settings.js';

// Helper function for analytics logging
function logAnalytics(message, data = null) {
    if (LOGGING.ENABLED && LOGGING.ADS) {
        if (data) {
            console.log(`[ANALYTICS] ${message}`, data);
        } else {
            console.log(`[ANALYTICS] ${message}`);
        }
    }
}

/**
 * Check if analytics tracking is allowed (settings + cookie consent)
 * @returns {boolean} True if analytics tracking is enabled and user has consented
 */
function isAnalyticsAllowed() {
    // Check if analytics tracking is enabled in settings
    if (!ANALYTICS_SETTINGS?.GOOGLE_ANALYTICS_ENABLED) {
        return false;
    }

    // Check if user has accepted analytics cookies (GDPR compliance)
    if (window.CookieConsent && typeof window.CookieConsent.acceptedCategory === 'function') {
        return window.CookieConsent.acceptedCategory('analytics');
    }

    // If CookieConsent is not available, default to false (privacy-first approach)
    return false;
}

/**
 * Track analytics events via Google Tag Manager dataLayer
 * Only tracks if:
 * 1. GOOGLE_ANALYTICS_ENABLED is true in ANALYTICS_SETTINGS
 * 2. User has accepted analytics cookies (GDPR compliance)
 */
export function trackAdEvent(eventType, data = {}) {
    // Check if analytics tracking is allowed (settings + consent)
    if (!isAnalyticsAllowed()) {
        // Silently skip tracking if disabled or no consent (no console output to avoid spam)
        return;
    }

    try {
        // Ensure dataLayer exists
        window.dataLayer = window.dataLayer || [];

        // Create event object
        const eventData = {
            event: `ads_${eventType}`,
            ads_type: data.type || 'unknown',
            ads_design: data.design || 'unknown',
            ads_id: data.id || null,
            ads_partner: data.affiliate?.partner || null,
            ads_affiliate_id: data.affiliate?.id || null,
            ads_timestamp: new Date().toISOString(),
            ads_page_url: window.location.href,
            ads_page_title: document.title,
            ads_user_agent: navigator.userAgent
        };

        // Add affiliate-specific data if available
        if (data.affiliate) {
            eventData.ads_affiliate_title = data.affiliate.title;
            eventData.ads_affiliate_url = data.affiliate.url;
            eventData.ads_affiliate_price = data.affiliate.price;
            eventData.ads_affiliate_rating = data.affiliate.rating;
        }

        // Push to dataLayer
        window.dataLayer.push(eventData);

        logAnalytics(`Event tracked: ${eventType}`, eventData);

    } catch (error) {
        console.error('Failed to track ad event:', error);
    }
}

/**
 * Track ad impression
 */
export function trackAdImpression(type, id, design, affiliate = null) {
    trackAdEvent('impression', { type, id, design, affiliate });
}

/**
 * Track ad click
 */
export function trackAdClick(type, id, design, affiliate = null) {
    trackAdEvent('click', { type, id, design, affiliate });
}

/**
 * Track ad view (when ad becomes visible)
 */
export function trackAdView(type, id, design, affiliate = null) {
    trackAdEvent('view', { type, id, design, affiliate });
}

/**
 * Track ad conversion (for affiliate sales)
 */
export function trackAdConversion(type, id, design, affiliate = null, conversionData = {}) {
    const eventData = {
        type,
        id,
        design,
        affiliate,
        conversion_value: conversionData.value || null,
        conversion_currency: conversionData.currency || 'USD',
        conversion_id: conversionData.id || null
    };

    trackAdEvent('conversion', eventData);
}

/**
 * Set up intersection observer for view tracking
 */
export function setupViewTracking() {
    if (!window.IntersectionObserver) {
        console.warn('IntersectionObserver not supported, view tracking disabled');
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const adElement = entry.target;
                const adType = adElement.dataset.adType;
                const adDesign = adElement.dataset.design;
                const adId = adElement.dataset.affiliateId;

                // Only track once per ad
                if (!adElement.dataset.viewTracked) {
                    adElement.dataset.viewTracked = 'true';

                    // Get affiliate data if available
                    let affiliate = null;
                    if (adType === 'affiliate' && adId) {
                        affiliate = {
                            id: adId,
                            partner: adElement.dataset.partner,
                            title: adElement.dataset.title,
                            url: adElement.dataset.url
                        };
                    }

                    trackAdView(adType, adId, adDesign, affiliate);
                }
            }
        });
    }, {
        threshold: 0.5, // 50% of ad must be visible
        rootMargin: '0px 0px -50px 0px' // Start tracking 50px before ad enters viewport
    });

    // Observe all ad elements
    const adElements = document.querySelectorAll('[data-ad-type]');
    adElements.forEach(element => observer.observe(element));

    // Set up mutation observer for dynamically added ads
    const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const newAdElements = node.querySelectorAll('[data-ad-type]');
                    newAdElements.forEach(element => observer.observe(element));
                }
            });
        });
    });

    mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
    });
}

/**
 * Track affiliate link clicks with conversion tracking
 */
export function setupAffiliateClickTracking() {
    document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href*="affiliate"], a[data-affiliate-id]');

        if (link) {
            const adElement = link.closest('[data-ad-type]');
            if (adElement) {
                const adType = adElement.dataset.adType;
                const adDesign = adElement.dataset.design;
                const adId = adElement.dataset.affiliateId;

                // Get affiliate data
                let affiliate = null;
                if (adType === 'affiliate' && adId) {
                    affiliate = {
                        id: adId,
                        partner: adElement.dataset.partner,
                        title: adElement.dataset.title,
                        url: link.href
                    };
                }

                // Track click
                trackAdClick(adType, adId, adDesign, affiliate);

                // Add conversion tracking parameters to URL if it's an affiliate link
                if (adType === 'affiliate' && affiliate) {
                    const url = new URL(link.href);
                    url.searchParams.set('utm_source', 'affiliate');
                    url.searchParams.set('utm_medium', 'ads');
                    url.searchParams.set('utm_campaign', affiliate.partner);
                    url.searchParams.set('utm_content', adId);
                    url.searchParams.set('ref', 'ads_system');

                    // Update link href
                    link.href = url.toString();
                }
            }
        }
    });
}

/**
 * Initialize analytics tracking
 * Only initializes if:
 * 1. GOOGLE_ANALYTICS_ENABLED is true in ANALYTICS_SETTINGS
 * 2. User has accepted analytics cookies (GDPR compliance)
 * 
 * Note: This will be called on page load, but tracking will only happen if consent is given.
 * If consent is given later, the user can re-initialize by calling this function again.
 */
export function initializeAdsAnalytics() {
    // Check if analytics tracking is enabled in settings
    if (!ANALYTICS_SETTINGS?.GOOGLE_ANALYTICS_ENABLED) {
        // Silently skip initialization if disabled
        return;
    }

    // Set up view tracking (will check consent on each view)
    setupViewTracking();

    // Set up click tracking (will check consent on each click)
    setupAffiliateClickTracking();

    // Only log if consent is already given
    if (isAnalyticsAllowed()) {
        logAnalytics('Analytics initialized');
    } else {
        // Log that analytics is ready but waiting for consent
        if (LOGGING.ENABLED && LOGGING.ADS) {
            logAnalytics('Analytics ready (waiting for cookie consent)');
        }
    }
}

/**
 * Get analytics data for reporting
 */
export function getAdsAnalyticsData() {
    // This would typically fetch data from your analytics platform
    // For now, we'll return a placeholder
    return {
        impressions: 0,
        clicks: 0,
        conversions: 0,
        ctr: 0,
        revenue: 0
    };
}

/**
 * Track custom ad events
 */
export function trackCustomAdEvent(eventName, data = {}) {
    trackAdEvent(eventName, data);
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAdsAnalytics);
} else {
    initializeAdsAnalytics();
} 